import { useState } from "react";
import { Col, Form, Nav, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { CompanyType, ConfigDesignTemp, LinkFields } from "../../models/company";

interface CompanyLinksProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  setCompany: (company: CompanyType) => void;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

type LangKey = "nl" | "fr" | "en";
type LinkKey = keyof LinkFields;

const LINK_FIELDS: { key: LinkKey; label: string }[] = [
  { key: "website_url", label: "Website URL" },
  { key: "contact_page", label: "Contact" },
  { key: "buy_page", label: "Listings - For Sale" },
  { key: "rent_page", label: "Listings - For Rent" },
  { key: "sell_page", label: "Selling" },
  { key: "let_page", label: "Renting" },
  { key: "valuation_page", label: "Valuation" },
  { key: "blog_page", label: "Blog" },
  { key: "team_page", label: "Team" },
  { key: "about_page", label: "About Us" },
  { key: "faq_page", label: "FAQ" },
  { key: "jobs_page", label: "Jobs" },
  { key: "privacy_policy_page", label: "Privacy Policy" },
  { key: "conditions_page", label: "Conditions" },
  { key: "cookie_policy_page", label: "Cookie Policy" },
];

const LANG_LABELS: Record<LangKey, string> = { nl: "NL", fr: "FR", en: "EN" };

/** Migrate old flat links structure (links.website_url) to new nested (links.nl.website_url) */
function migrateLinks(links: ConfigDesignTemp["links"]): ConfigDesignTemp["links"] {
  if (!links) return links;
  const linkFieldKeys = LINK_FIELDS.map((f) => f.key);
  const isOldFormat = linkFieldKeys.some((k) => k in links);
  if (!isOldFormat) return links;
  const nl: LinkFields = {};
  for (const key of linkFieldKeys) {
    const val = (links as any)[key];
    if (val) nl[key] = val;
  }
  return { nl };
}

function CompanyLinks({
  company,
  originalCompany,
  setCompany,
  hasFieldChanged,
  ChangedIndicator,
  readOnly = false,
}: CompanyLinksProps) {
  const configuredLanguages = (company.info?.languages ?? ["nl"]) as LangKey[];
  const availableLangs = configuredLanguages.length > 0 ? configuredLanguages : (["nl"] as LangKey[]);

  const [selectedLang, setSelectedLang] = useState<LangKey>("nl");

  const rawLinks = company.config_design_temp?.links;
  const links = migrateLinks(rawLinks) ?? {};

  const rawOriginalLinks = originalCompany?.config_design_temp?.links;
  const originalLinks = migrateLinks(rawOriginalLinks) ?? {};

  const nlLinks: LinkFields = links.nl ?? {};
  const currentLangLinks: LinkFields = links[selectedLang] ?? {};

  const updateLink = (lang: LangKey, key: LinkKey, value: string) => {
    setCompany({
      ...company,
      config_design_temp: {
        ...company.config_design_temp,
        links: {
          ...links,
          [lang]: {
            ...links[lang],
            [key]: value,
          },
        },
      },
    });
  };

  const copyFromNl = (key: LinkKey) => {
    const nlValue = nlLinks[key] ?? "";
    updateLink(selectedLang, key, nlValue);
  };

  const clearOverride = (key: LinkKey) => {
    const updated = { ...(links[selectedLang] ?? {}) };
    delete updated[key];
    setCompany({
      ...company,
      config_design_temp: {
        ...company.config_design_temp,
        links: {
          ...links,
          [selectedLang]: updated,
        },
      },
    });
  };

  const normalizeForCompare = (val: any): string => {
    return JSON.stringify(val, (_key, v) => {
      if (v === null || v === "" || v === undefined) return undefined;
      if (Array.isArray(v) && v.length === 0) return undefined;
      if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return undefined;
      return v;
    });
  };

  const hasLangChanges = (lang: LangKey): boolean => {
    return normalizeForCompare(links[lang]) !== normalizeForCompare(originalLinks[lang]);
  };

  return (
    <Form>
      {availableLangs.length > 0 && (
        <Nav variant="tabs" className="mb-4">
          {availableLangs.map((lang) => (
            <Nav.Item key={lang}>
              <Nav.Link
                active={selectedLang === lang}
                onClick={() => setSelectedLang(lang)}
                style={{ cursor: "pointer" }}
              >
                {LANG_LABELS[lang]}
                {hasLangChanges(lang) && <ChangedIndicator />}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      )}

      <Row>
        {LINK_FIELDS.map((field) => {
          const isNlTab = selectedLang === "nl";
          const currentValue = currentLangLinks[field.key] ?? "";
          const nlValue = nlLinks[field.key] ?? "";
          const hasOverride = !isNlTab && field.key in (links[selectedLang] ?? {});
          const displayValue = hasOverride ? currentValue : isNlTab ? currentValue : "";
          const placeholder = isNlTab ? "https://..." : nlValue || "https://...";
          const isChanged = hasFieldChanged(
            (links[selectedLang] ?? {})[field.key] || undefined,
            (originalLinks[selectedLang] ?? {})[field.key] || undefined,
          );

          return (
            <Col md={6} key={field.key} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontSize: "0.85rem" }}>
                  {field.label}
                  {isChanged && <ChangedIndicator />}
                </Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control
                    type="url"
                    value={displayValue}
                    onChange={(e) => updateLink(selectedLang, field.key, e.target.value)}
                    placeholder={placeholder}
                    readOnly={readOnly}
                  />
                  {!isNlTab && !readOnly && (
                    <>
                      {hasOverride ? (
                        <OverlayTrigger overlay={<Tooltip>Reset to NL value</Tooltip>}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary px-2"
                            onClick={() => clearOverride(field.key)}
                            title="Reset to NL"
                          >
                            <i className="bi bi-arrow-counterclockwise" />
                          </button>
                        </OverlayTrigger>
                      ) : (
                        <OverlayTrigger overlay={<Tooltip>Copy from NL</Tooltip>}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary px-2"
                            onClick={() => copyFromNl(field.key)}
                            disabled={!nlValue}
                            title="Copy from NL"
                          >
                            <i className="bi bi-files" />
                          </button>
                        </OverlayTrigger>
                      )}
                    </>
                  )}
                </div>
              </Form.Group>
            </Col>
          );
        })}
      </Row>
    </Form>
  );
}

export default CompanyLinks;
